// Legend: colour bar with 5 tick labels, unit, "vs. <reference>" in diff view, metric label,
// Methods link and the attribution line. Colours come only from the computed ColorStops.
import type { PlzRecord, RegistryMetric } from "../registry";
import { colorScale, legendTicks, type ColorStops } from "../scales";
import { formatBandLabel, type ViewMode } from "../logic";
import { escapeHtml } from "./format";

export interface ReachLegendInfo {
  /** Active band in minutes; callers should omit this whole object when it is 0 ("Any"). */
  bandMinutes: number;
  reachableCount: number;
  total: number;
  refPlz: string;
  refRecord: PlzRecord | undefined;
}

export interface LegendData {
  metric: RegistryMetric;
  view: ViewMode;
  stops: ColorStops;
  domain: [number, number];
  refPlz: string;
  refRecord: PlzRecord | undefined;
  /** Present (and only rendered) when the "Reachable within" filter is active. Applies to any
   * metric's legend, not just the pairwise one — the filter dims every metric. */
  reach?: ReachLegendInfo;
  /** Additional credit line, e.g. for travel-time data, appended to the attribution. */
  extraAttribution?: string;
}

const ATTRIBUTION = "Climate data © MeteoSwiss (CC BY 4.0) · Postal-code perimeters and elevation © swisstopo";

export function renderLegend(container: HTMLElement, data: LegendData): void {
  const scale = colorScale(data.stops);
  const ticks = legendTicks(data.domain, 5);
  const [lo, hi] = data.domain;
  const steps = 20;
  const gradient = Array.from({ length: steps }, (_, i) => {
    const t = i / (steps - 1);
    return `${scale(lo + (hi - lo) * t)} ${(t * 100).toFixed(1)}%`;
  }).join(", ");

  const sign = (v: number) => (data.view === "diff" && v > 0 ? "+" : "");
  const tickSpans = ticks.map((t) => `<span>${sign(t)}${t.toFixed(data.metric.decimals)}</span>`).join("");

  const subtitle =
    data.metric.kind === "pairwise" && data.refRecord
      ? `from ${escapeHtml(data.refPlz)} ${escapeHtml(data.refRecord.name)}`
      : data.view === "diff" && data.refRecord
        ? `vs. ${escapeHtml(data.refPlz)} ${escapeHtml(data.refRecord.name)}`
        : "absolute value";

  const methodsLink = data.metric.method_url
    ? `<a href="${escapeHtml(data.metric.method_url)}" target="_blank" rel="noopener">Methods</a>`
    : "";

  const reachLine =
    data.reach && data.reach.bandMinutes > 0
      ? `<div class="legend-reach">showing ${data.reach.reachableCount} of ${data.reach.total} PLZs within ${formatBandLabel(data.reach.bandMinutes)} by public transport from ${escapeHtml(data.reach.refPlz)}${data.reach.refRecord ? " " + escapeHtml(data.reach.refRecord.name) : ""}</div>`
      : "";

  container.innerHTML = `
    <div class="legend-title">${escapeHtml(data.metric.label)} <span class="legend-unit">${escapeHtml(data.metric.unit)}</span></div>
    <div class="legend-bar" style="background: linear-gradient(to right, ${gradient})"></div>
    <div class="legend-ticks">${tickSpans}</div>
    <div class="legend-sub">${subtitle}</div>
    ${reachLine}
    <div class="legend-footer">
      <span>${methodsLink}</span>
      <span class="legend-attribution">${ATTRIBUTION}${data.extraAttribution ? " · " + escapeHtml(data.extraAttribution) : ""}</span>
    </div>
  `;
}
